import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { getCookie } from '../../utils';
import { addAlert } from '../../alerts';
import { prompt } from '../../prompts';

const CHANGED_CELL_CLASS = 'is-changed';
const CONFIG_COLUMN_BASE_WIDTH = 70;
const CONFIG_COLUMN_CHAR_WIDTH = 8;
const CONFIG_COLUMN_EXTRA_PADDING = 38;

function getConfigHeaderWidth(title, columnsCount = 1) {
    const safeColumnsCount = Math.max(columnsCount, 1);
    const titleWidth =
        CONFIG_COLUMN_BASE_WIDTH +
        String(title || '').length * CONFIG_COLUMN_CHAR_WIDTH +
        CONFIG_COLUMN_EXTRA_PADDING;

    return Math.ceil(titleWidth / safeColumnsCount);
}

// Helper for formatting state cells
function stateFormatter(cell, formatterParams, onRendered) {
    const value = cell.getValue();
    const element = cell.getElement();
    element.classList.remove('state-p', 'state-s', 'state-n');

    if (value === 'P') {
        element.classList.add('state-p');
        element.title = gettext('Przetwarzane w procesie');
    } else if (value === 'S') {
        element.classList.add('state-s');
        element.title = gettext('Wyświetlane w sklepie');
    } else {
        element.classList.add('state-n');
        element.title = gettext('Nie dotyczy');
    }
    return value;
}

// Generic Config Table Initializer
export function initializeConfigTable({
    containerId,
    saveButtonId,
    dataApiUrl,
    saveApiUrl,
}) {
    let pendingChanges = {};
    let tabulatorInstance = null;
    let _rawData = null;
    let saveButton = document.getElementById(saveButtonId);
    const tableContainer = document.getElementById(containerId);

    if (!tableContainer) {
        console.error(`Config table container #${containerId} not found.`);
        return;
    }

    // --- Cell Click Handler ---
    function handleConfigCellClick(e, cell) {
        const currentVal = cell.getValue();
        const fieldName = cell.getRow().getData().field_name;
        const columnDef = cell.getColumn().getDefinition();
        const configMeta = columnDef._config_meta;

        const cycle = { N: 'P', P: 'S', S: 'N' };
        const nextVal = cycle[currentVal] || 'P';

        cell.setValue(nextVal, true);

        const payload = {
            group_value: configMeta.group_value,
            type_value: configMeta.type_value,
            field_name: fieldName,
            state: nextVal,
        };

        const changeKey = `${configMeta.group_value}-${configMeta.type_value || 'None'}-${fieldName}`;
        pendingChanges[changeKey] = payload;

        cell.getElement().classList.add(CHANGED_CELL_CLASS);
        if (saveButton) {
            saveButton.disabled = false;
        }
    }

    // --- Save Changes Handler ---
    async function saveAllChanges() {
        const changesToSave = Object.values(pendingChanges);
        if (changesToSave.length === 0) {
            addAlert('info', gettext('Brak zmian do zapisania.'));
            return;
        }

        const stateLabels = {
            P: gettext('Proces'),
            S: gettext('Sklep'),
            N: gettext('Nie dotyczy'),
        };

        const groupedChangesForPrompt = {};
        changesToSave.forEach((change) => {
            const groupTypeKey = `${change.group_value}---${change.type_value || 'GENERAL_NULL_TYPE'}`;

            if (!groupedChangesForPrompt[groupTypeKey]) {
                groupedChangesForPrompt[groupTypeKey] = {
                    group_value: change.group_value,
                    type_value: change.type_value,
                    type_display: change.type_value
                        ? ` (${change.type_value})`
                        : '',
                    field_changes: [],
                };
            }

            const fieldLabel = _rawData.fields[change.field_name].label;
            const stateDisplay = stateLabels[change.state] || change.state;

            groupedChangesForPrompt[groupTypeKey].field_changes.push({
                field_label: fieldLabel,
                state_display: stateDisplay,
            });
        });

        const changesHtml = Object.values(groupedChangesForPrompt)
            .map((groupTypeItem) => {
                const fieldChangesHtml = groupTypeItem.field_changes
                    .map((fieldChange) => {
                        return `<li>${gettext('Pole')}: <strong>${fieldChange.field_label}</strong> → ${gettext('Nowy stan')}:  <strong>${fieldChange.state_display}</strong></li>`;
                    })
                    .join('');

                return `
                <div class="change-group-summary">
                    <h5>
                        ${groupTypeItem.group_value}${groupTypeItem.type_display}
                    </h5>
                    <ul class="field-changes-list">
                        ${fieldChangesHtml}
                    </ul>
                </div>
            `;
            })
            .join('');

        const result = await prompt({
            title: gettext('Potwierdź zapisanie zmian'),
            html: `
                    <div class="config-changes-container">
                        ${changesHtml}
                    </div>
                `,
            width: 'auto',
        });
        if (!result.isConfirmed) return;

        if (saveButton) {
            saveButton.disabled = true;
            saveButton.textContent = gettext('Zapisywanie...');
        }

        try {
            const response = await fetch(saveApiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFTOKEN': getCookie('csrftoken'),
                },
                body: JSON.stringify(changesToSave),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(
                    data.message ||
                        gettext('Nie udało się zapisać zmian. Kod błędu: ') +
                            response.status,
                );
            }

            addAlert(
                'success',
                data.message || gettext('Zmiany zapisane pomyślnie.'),
            );
            pendingChanges = {};

            tabulatorInstance.getRows().forEach((row) => {
                row.getCells().forEach((cellComponent) => {
                    cellComponent
                        .getElement()
                        .classList.remove(CHANGED_CELL_CLASS);
                });
            });
        } catch (error) {
            console.error('Error saving field states:', error);
            addAlert(
                'error',
                error.message ||
                    gettext('Błąd zapisu zmian. Spróbuj ponownie.'),
            );
        } finally {
            if (saveButton) {
                saveButton.textContent = gettext('Zapisz zmiany');
                saveButton.disabled = Object.keys(pendingChanges).length === 0;
            }
        }
    }

    if (saveButton) {
        saveButton.addEventListener('click', saveAllChanges);
    }

    // --- Data Loading & Table Build ---
    fetch(dataApiUrl)
        .then((response) => {
            if (!response.ok) {
                throw new Error(
                    `Network response was not ok (${response.status})`,
                );
            }
            return response.json();
        })
        .then((rawData) => {
            _rawData = rawData;
            const tabulatorColumns = buildTabulatorColumns(
                rawData.groups,
                handleConfigCellClick,
            );
            const tabulatorData = buildTabulatorData(
                rawData.fields,
                rawData.groups,
                rawData.config_states,
            );
            tabulatorInstance = new Tabulator(`#${containerId}`, {
                data: tabulatorData,
                columns: tabulatorColumns,
                layout: 'fitData',
                groupBy: 'group_name',
                groupHeader: function (value, count, data, group) {
                    return value;
                },
                height: 'calc(100vh - 35rem)',
                maxHeight: '95rem',
            });
        })
        .catch((error) => {
            console.error('Failed to load or process config UI data:', error);
            addAlert(
                'error',
                'Błąd wczytywania danych. Odśwież stronę lub skontaktuj się z administratorem.',
            );
        });
}

function buildTabulatorColumns(groups, cellClickHandler) {
    const columns = [
        {
            title: 'Pole Formularza',
            field: 'field_label',
            frozen: true,
            minWidth: 200,
        },
    ];

    groups.forEach((group) => {
        const groupColumnsCount = group.types.length || 1;
        const groupTitleMinWidth = getConfigHeaderWidth(
            group.value,
            groupColumnsCount,
        );
        const groupColumn = {
            title: group.value,
            columns: [],
            resizable: false,
        };

        const groupLevelKey = `group_${group.id}_type_None`;
        if (group.types.length === 0) {
            groupColumn.columns.push({
                title: '',
                field: groupLevelKey,
                width: groupTitleMinWidth,
                resizable: false,
                hozAlign: 'center',
                editor: false,
                formatter: stateFormatter,
                cellClick: cellClickHandler,
                cssClass: 'config-cell',
                _config_meta: {
                    group_id: group.id,
                    type_id: null,
                    group_value: group.value,
                    type_value: null,
                },
            });
        }

        group.types.forEach((type) => {
            const typeLevelKey = `group_${group.id}_type_${type.id}`;
            const columnWidth = Math.max(
                getConfigHeaderWidth(type.value),
                groupTitleMinWidth,
            );

            groupColumn.columns.push({
                title: type.value,
                field: typeLevelKey,
                width: columnWidth,
                resizable: false,
                hozAlign: 'center',
                editor: false,
                formatter: stateFormatter,
                cellClick: cellClickHandler,
                cssClass: 'config-cell',
                _config_meta: {
                    group_id: group.id,
                    type_id: type.id,
                    group_value: group.value,
                    type_value: type.value,
                },
            });
        });

        columns.push(groupColumn);
    });

    return columns;
}

function buildTabulatorData(fields, groups, configStates) {
    const data = [];
    const fieldNames = Object.keys(fields);

    fieldNames.forEach((fieldName) => {
        const fieldInfo = fields[fieldName];
        const row = {
            field_name: fieldName,
            field_label: fieldInfo.label,
            group_name: fieldInfo.group,
        };

        groups.forEach((group) => {
            const groupLevelKey = `group_${group.id}_type_None`;
            const groupConfig = configStates[groupLevelKey] || {};
            row[groupLevelKey] = groupConfig[fieldName] || 'P';

            group.types.forEach((type) => {
                const typeLevelKey = `group_${group.id}_type_${type.id}`;
                const typeConfig = configStates[typeLevelKey] || {};
                row[typeLevelKey] = typeConfig[fieldName] || 'P';
            });
        });
        data.push(row);
    });
    return data;
}

export function initializeKEPConfigUI() {
    initializeConfigTable({
        containerId: 'config-table',
        saveButtonId: 'save-kep-changes-btn',
        dataApiUrl: '/api/config/kep/config-data/',
        saveApiUrl: '/api/config/kep/bulk-update-field-states/',
    });
}

export function initializeKEUConfigUI() {
    initializeConfigTable({
        containerId: 'config-table',
        saveButtonId: 'save-keu-changes-btn',
        dataApiUrl: '/api/config/keu/config-data/',
        saveApiUrl: '/api/config/keu/bulk-update-field-states/',
    });
}
